<script lang="ts">
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import GhostButton from "../../../_shared/_modules/ghost-button"
  import type {
    OrganizationAddress,
    OrganizationPresentation,
  } from "../../../../api/public/presentation"

  const { organization, loading, error, load } = $props<{
    organization: OrganizationPresentation | null
    loading: boolean
    error: string | null
    load: () => Promise<void> | void
  }>()

  const normalizeText = (value?: string | null): string | null => {
    const text = value?.trim()

    return text || null
  }

  const displayName = $derived(
    normalizeText(organization?.public_name) ?? normalizeText(organization?.name) ?? "dem Betrieb",
  )

  const formatAddressLines = (address?: OrganizationAddress | null): string[] => {
    if (!address) return []

    const streetLine = [address.street, address.house_number]
      .map(normalizeText)
      .filter(Boolean)
      .join(" ")
    const cityLine = [address.zip, address.city].map(normalizeText).filter(Boolean).join(" ")

    return [address.address_extra, streetLine, cityLine, address.country]
      .map(normalizeText)
      .filter((line): line is string => Boolean(line))
  }

  const addressLines = $derived(formatAddressLines(organization?.address))

  const planFamilyId = $derived(normalizeText(organization?.plan_family_id)?.toLowerCase())
  const isKehrTalk = $derived(planFamilyId === "kehrtalk")
  const isKehrChatOrTalk = $derived(planFamilyId === "kehrchat" || isKehrTalk)

  const channelList = $derived.by(() => {
    const base = ["E-Mail", "Mobile Nachricht", "Terminbrief", "öffentliches Terminportal"]

    if (isKehrTalk) {
      return [...base, "Online-Terminbot", "telefonische Assistenz"]
    }

    if (isKehrChatOrTalk) return [...base, "Online-Terminbot"]

    return base
  })

  const appointmentCommunicationPurpose = $derived(
    isKehrTalk
      ? "Terminankündigung per E-Mail, mobiler Nachricht, Brief oder Telefonassistenz"
      : "Terminankündigung per E-Mail, mobiler Nachricht oder Brief",
  )

  const submittedDataSources = $derived(
    isKehrTalk
      ? "Angaben, die Sie im Terminportal, im Online-Assistenten oder telefonisch mitteilen"
      : isKehrChatOrTalk
        ? "Angaben, die Sie im Terminportal oder im Online-Assistenten mitteilen"
        : "Angaben, die Sie im Terminportal mitteilen",
  )
</script>

{#if loading}
  <div class="state-block">
    <p>Datenschutzhinweise werden geladen…</p>
  </div>
{:else if error}
  <div class="state-block">
    <p>{error}</p>
    <GhostButton onclick={load}>Erneut laden</GhostButton>
  </div>
{:else}
  <section class="privacy-page" aria-label="Datenschutzhinweise">
    <SectionTitle
      title={`Datenschutzhinweise zur Terminabstimmung mit ${displayName}`}
      brightBackground={true}
      styleOuter="margin-bottom: 0;"
    />
    <p class="privacy-intro">
      Diese Hinweise erklären, wie {displayName} personenbezogene Daten im Rahmen der Terminplanung, Terminankündigung
      und Terminabstimmung verarbeitet.
    </p>

    <section class="privacy-section">
      <h3>Verantwortlicher Betrieb</h3>
      <p>{displayName}</p>
      {#if addressLines.length}
        <div class="line-group">
          {#each addressLines as line}
            <p>{line}</p>
          {/each}
        </div>
      {/if}
      {#if organization?.contact_email}
        <p
          >E-Mail: <a href={`mailto:${organization.contact_email}`}>{organization.contact_email}</a
          ></p
        >
      {/if}
      {#if organization?.contact_phone}
        <p
          >Telefon: <a href={`tel:${organization.contact_phone}`}>{organization.contact_phone}</a
          ></p
        >
      {/if}
    </section>

    <section class="privacy-section">
      <h3>Einsatz von KehrBlatt</h3>
      <p>
        Für die Terminplanung und Terminabstimmung nutzt {displayName} die Plattform KehrBlatt der KontextWerk
        UG (haftungsbeschränkt). KehrBlatt stellt die technische Plattform bereit und verarbeitet personenbezogene
        Daten im Auftrag von {displayName}. Die Verantwortung für die Terminabstimmung und die
        Verarbeitung der Kundendaten liegt bei {displayName}.
      </p>
    </section>

    <section class="privacy-section">
      <h3>Zwecke der Verarbeitung</h3>
      <p>
        Ihre Daten werden verarbeitet, um Schornsteinfegertermine vorzubereiten, anzukündigen, zu
        bestätigen, zu verschieben, abzusagen oder Rückfragen und Anliegen zur Terminabstimmung zu
        bearbeiten.
      </p>
      <ul>
        <li>Planung und Durchführung von Schornsteinfegerterminen</li>
        <li>{appointmentCommunicationPurpose}</li>
        <li>Bearbeitung von Terminbestätigungen, Verschiebungswünschen und Absagen</li>
        <li
          >Zuordnung von Rückmeldungen zu Adresse, Liegenschaft, Wohnung, Ansprechpartner oder
          Auftrag</li
        >
        <li>Bearbeitung von Rückrufbitten, Kontaktdatenänderungen und sonstigen Anliegen</li>
        <li>Händischer Prüfung unklarer Rückmeldungen durch den Betrieb</li>
      </ul>
    </section>

    <section class="privacy-section">
      <h3>Verarbeitete Daten</h3>
      <p>Je nach Vorgang können insbesondere folgende Daten verarbeitet werden:</p>
      <ul>
        <li>Name und Kontaktdaten</li>
        <li>Adresse, Liegenschaft, Wohnung oder Einheit</li>
        <li>Angaben zu Ansprechpartnern, Eigentümern, Mietern oder Verwaltungen</li>
        <li>Termin- und Auftragsinformationen</li>
        <li>Rückmeldungen zur Terminbestätigung, Verschiebung oder Absage</li>
        <li>{submittedDataSources}</li>
        <li
          >technische Nutzungsdaten für Betrieb, Sicherheit, Fehleranalyse und Nachvollziehbarkeit</li
        >
      </ul>
    </section>

    <section class="privacy-section">
      <h3>Kommunikationswege</h3>
      <p>
        Je nach hinterlegten Kontaktdaten und eingesetztem Tarif kann die Terminabstimmung über
        folgende Wege erfolgen:
      </p>
      <ul>
        {#each channelList as channel}
          <li>{channel}</li>
        {/each}
      </ul>
      <p>Nicht jeder Betrieb nutzt alle genannten Wege.</p>
    </section>

    <section class="privacy-section">
      <h3>Rechtsgrundlagen</h3>
      <p>
        Die Verarbeitung erfolgt zur Organisation, Ankündigung und Durchführung von
        Schornsteinfegerarbeiten sowie zur Bearbeitung von Rückmeldungen und Anliegen. Je nach
        konkreter Tätigkeit kann die Verarbeitung insbesondere auf gesetzlichen Pflichten, der
        Durchführung eines Vertrags oder berechtigten Interessen an einer geordneten
        Terminabstimmung und Kundenkommunikation beruhen.
      </p>
      <p>
        Soweit für einzelne Funktionen eine Einwilligung erforderlich ist, wird diese gesondert
        eingeholt. Das gilt insbesondere, wenn im Rahmen einer Telefonassistenz eine Mitschrift des
        Gesprächs erstellt werden soll.
      </p>
    </section>

    <section class="privacy-section">
      <h3>Empfänger und Dienstleister</h3>
      <p>
        Zur technischen Durchführung der Terminabstimmung setzt {displayName} KehrBlatt als technischen
        Dienstleister ein. Je nach genutztem Funktionsumfang können technische Dienstleister für Hosting,
        E-Mail-Versand, mobile Nachrichten, Telefonie, Kalenderintegration, Karten-/Routenplanung oder
        digitale Assistenzsysteme eingebunden sein. KehrBlatt verarbeitet Daten nur im Rahmen des Auftrags
        und nach den vereinbarten Vorgaben.
      </p>
    </section>

    <section class="privacy-section">
      <h3>Speicherdauer</h3>
      <p>
        Die Daten werden nur so lange gespeichert, wie sie für Terminabstimmung, Durchführung der
        Arbeiten, Dokumentation, Nachvollziehbarkeit, gesetzliche Pflichten oder berechtigte
        betriebliche Zwecke erforderlich sind.
      </p>
    </section>

    <section class="privacy-section">
      <h3>Ihre Rechte</h3>
      <p>
        Sie haben nach Maßgabe der gesetzlichen Voraussetzungen das Recht auf Auskunft,
        Berichtigung, Löschung, Einschränkung der Verarbeitung, Widerspruch sowie
        Datenübertragbarkeit. Außerdem haben Sie das Recht, sich bei einer
        Datenschutzaufsichtsbehörde zu beschweren. Bei Fragen zur Verarbeitung Ihrer Daten wenden
        Sie sich bitte an {displayName}.
      </p>
    </section>
  </section>
{/if}

<style lang="less">
  .state-block {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;

    p {
      margin: 0;
    }
  }

  .privacy-page {
    display: flex;
    flex-direction: column;
    gap: clamp(1.5rem, 3vw, 2.4rem);
    max-width: 52rem;
  }

  .privacy-section,
  .line-group {
    display: flex;
    flex-direction: column;
  }

  .privacy-section {
    gap: 0.55rem;
    padding-left: 1rem;
    border-left: 2px solid var(--primary-100);
  }

  h3,
  p,
  ul {
    margin: 0;
  }

  h3 {
    color: var(--primary-100);
    font-size: 1rem;
    letter-spacing: 0;
    line-height: 1.2;
  }

  p,
  li,
  a {
    color: var(--text-invers-100);
    font-size: 1rem;
    line-height: 1.48;
  }

  .privacy-intro {
    color: var(--text-invers-100);
    font-size: 1rem;
    line-height: 1.48;
  }

  ul {
    display: grid;
    gap: 0.42rem;
    padding: 0;
    list-style: none;
  }

  li {
    position: relative;
    padding-left: 1rem;
  }

  li::before {
    content: "";
    position: absolute;
    top: 0.62em;
    left: 0;
    width: 0.36rem;
    height: 0.36rem;
    border: 1px solid var(--primary-100);
    background: color-mix(in srgb, var(--primary-100) 18%, transparent);
    transform: rotate(45deg);
  }

  a {
    text-decoration: underline;
    text-decoration-thickness: 0.08em;
    text-underline-offset: 0.16em;
  }
</style>
